
import { format, isValid, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { useRecords } from '../../../hooks/useRecords';
import { useBabies } from '../../../hooks/useBabies';

interface TodaySummaryProps {
  /** Fecha que se muestra en la etiqueta. Si no llega, se usa hoy. */
  date?: Date;
}

export default function TodaySummary({ date }: TodaySummaryProps) {
  const { selectedBaby } = useBabies();
  const { feedingRecords, sleepRecords, diaperRecords } = useRecords(selectedBaby?.id ?? null);

  // Se filtra por la hora REAL del evento, no por `created_at`, que es
  // cuándo se guardó la fila. Si un padre anota a medianoche la toma de
  // las 8 de la noche, cuenta para el día correcto y el "hace X" sale
  // bien. Antes, anotar tarde inventaba tiempos que nunca pasaron.
  const todayRecords = {
    feeding: feedingRecords.filter(record => isToday(new Date(record.start_time))),
    sleep: sleepRecords.filter(record => isToday(new Date(record.start_time))),
    diaper: diaperRecords.filter(record => isToday(new Date(record.time)))
  };

  const hace = (desde: string) => {
    const minutos = Math.floor((Date.now() - new Date(desde).getTime()) / 60000);
    if (minutos < 1) return 'Recién';
    if (minutos < 60) return `Hace ${minutos} min`;
    const horas = Math.floor(minutos / 60);
    const resto = minutos % 60;
    return resto === 0 ? `Hace ${horas}h` : `Hace ${horas}h ${resto}min`;
  };

  // Calcular estadísticas reales
  const getLastFeedTime = () => {
    if (todayRecords.feeding.length === 0) return 'Sin registros';
    return hace(todayRecords.feeding[0].start_time);
  };

  const getLastDiaperTime = () => {
    if (todayRecords.diaper.length === 0) return 'Sin registros';
    const lastDiaper = todayRecords.diaper[0];
    const diaperTime = new Date(lastDiaper.time);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - diaperTime.getTime()) / (1000 * 60));

    if (diffInMinutes < 60) {
      return `Hace ${diffInMinutes} min`;
    } else {
      const hours = Math.floor(diffInMinutes / 60);
      return `Hace ${hours}h ${diffInMinutes % 60}min`;
    }
  };

  const getTotalSleepToday = () => {
    if (todayRecords.sleep.length === 0) return 'Sin registros';

    // La duración se calcula del inicio y el fin. Antes se sumaba un
    // campo `duration_minutes` que no existe en la base, así que el
    // total de sueño del día daba siempre "Sin registros" por más
    // siestas que se hubieran anotado.
    const totalMinutes = todayRecords.sleep.reduce((total, record) => {
      if (!record.end_time) return total;   // siesta todavía en curso
      const minutos = Math.round(
        (new Date(record.end_time).getTime() - new Date(record.start_time).getTime()) / 60000
      );
      return total + Math.max(0, minutos);
    }, 0);

    if (totalMinutes === 0) return 'Sin registros';
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours === 0) {
      return `${minutes}min`;
    }
    return `${hours}h ${minutes}min`;
  };

  const getFeedingCount = () => {
    return todayRecords.feeding.length;
  };

  // Validar la fecha antes de formatearla
  const formatDate = (dateToFormat?: Date) => {
    if (!dateToFormat || !isValid(dateToFormat)) {
      return format(new Date(), 'dd MMM', { locale: es });
    }
    return format(dateToFormat, 'dd MMM', { locale: es });
  };

  // Si no hay bebé seleccionado, mostrar mensaje
  if (!selectedBaby) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-6 shadow-lg">
        <div className="flex items-center justify-center py-8">
          <p className="text-gray-500 text-center">Selecciona o agrega un bebé para ver el resumen</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800">Resumen de Hoy</h2>
        <span className="text-sm text-gray-600 bg-pink-100 px-3 py-1 rounded-full">
          {formatDate(date)}
        </span>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-pink-100 to-pink-200 rounded-xl p-4">
          <div className="flex items-center mb-2">
            <i className="ri-heart-fill text-pink-600 text-lg mr-2"></i>
            <span className="text-sm font-medium text-pink-800">Última Toma</span>
          </div>
          <p className="text-lg font-bold text-pink-800">{getLastFeedTime()}</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-4">
          <div className="flex items-center mb-2">
            <i className="ri-drop-fill text-blue-600 text-lg mr-2"></i>
            <span className="text-sm font-medium text-blue-800">Último Pañal</span>
          </div>
          <p className="text-lg font-bold text-blue-800">{getLastDiaperTime()}</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl p-4">
          <div className="flex items-center mb-2">
            <i className="ri-moon-fill text-purple-600 text-lg mr-2"></i>
            <span className="text-sm font-medium text-purple-800">Sueño Total</span>
          </div>
          <p className="text-lg font-bold text-purple-800">{getTotalSleepToday()}</p>
        </div>
        
        <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-xl p-4">
          <div className="flex items-center mb-2">
            <i className="ri-restaurant-fill text-green-600 text-lg mr-2"></i>
            <span className="text-sm font-medium text-green-800">Tomas Hoy</span>
          </div>
          <p className="text-lg font-bold text-green-800">{getFeedingCount()} tomas</p>
        </div>
      </div>
    </div>
  );
}
